import { type ProfileLayout_userAccount$key } from '#app/__generated__/relay/ProfileLayout_userAccount.graphql.js';
import { type ProfileLayout_userProfile$key } from '#app/__generated__/relay/ProfileLayout_userProfile.graphql.js';
import { GridColumn } from '#app/components/Grid/GridColumn.js';
import { ProfileImageCropperContextProvider } from '#app/components/ProfileImageCropper/context/ProfileImageCropperContext.js';
import { RoutingTabs } from '#app/components/RoutingTabs/RoutingTabs.js';
import { resolveRecommendationsVisibility } from '#app/features/profile/components/ProfileRecommendations/utils.js';
import { useBreakpoint } from '#app/hooks/useBreakpoint.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useMessageSentModal } from '#app/hooks/useMessageSentModal.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { useWorkspaces } from '#app/hooks/useWorkspaces.js';
import { MainLayout } from '#app/layouts/MainLayout/MainLayout.js';
import { AccountDoesNotExist } from '../components/AccountDoesNotExist/AccountDoesNotExist.js';
import { AdminActions } from '../components/AdminActions/AdminActions.js';
import { ExplorePortfoliosCallout } from '../components/ExplorePortfoliosCallout/ExplorePortfoliosCallout.js';
import {
  ProfileTitle,
  ProfileTitleContainer,
} from '../components/InlineHeadlineEditor/InlineHeadlineEditor.styles.js';
import { NewProfileSidebar } from '../components/NewProfileSidebar/NewProfileSidebar.js';
import {
  Main,
  ProfileBody,
  ProfileContent,
  RelativePositionContainer,
  Section,
} from './ProfileLayout.styles.js';
import { ProfileRoutingTabs } from './ProfileRoutingTabs.js';
import { DiscoveryInsightsButton } from './ProfileTabs/DiscoveryInsightsButton.js';
import { MobileDiscoveryInsightsButton } from './ProfileTabs/MobileDiscoveryInsightsButton.js';
import { OpportunitiesTab } from './ProfileTabs/OpportunitiesTab.js';
import { RecommendationsTab } from './ProfileTabs/RecommendationsTab.js';
import { ServicesTab } from './ProfileTabs/ServicesTab.js';
import { WorkTab } from './ProfileTabs/WorkTab.js';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import {
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

const UncontrolledHeadlineEditor = lazy(async () => {
  return {
    default: (
      await import(
        '../components/InlineHeadlineEditor/UncontrolledHeadlineEditor.js'
      )
    ).UncontrolledHeadlineEditor,
  };
});

const ProfileImageCropperModal = lazy(async () => {
  return {
    default: (
      await import('#app/components/ProfileImageCropper/ProfileImageCropper.js')
    ).ProfileImageCropper,
  };
});

const ProfileCompletionChecklist = lazy(async () => {
  return {
    default: (
      await import(
        '#app/components/ProfileCompletionChecklist/ProfileCompletionChecklist.js'
      )
    ).ProfileCompletionChecklist,
  };
});

const FinalListItem = styled.li`
  ${({ theme }) => css`
    display: none;
    margin-left: auto;
    padding-bottom: 12px;
    margin-right: 0 !important;
    border-bottom: 1px solid ${theme.colorsV2.gray20};

    ${theme.mediaQueries.md} {
      display: block;
    }

    ${theme.mediaQueries.lg} {
      display: block;
      padding-right: 40px;
    }

    ${theme.mediaQueries.xl} {
      padding-right: 16px;
    }
  `}
`;

const MobileViewInsightsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;

    ${theme.mediaQueries.md} {
      display: none;
    }
  `}
`;

type ProfileLayoutProps = {
  readonly accountRef: null | ProfileLayout_userAccount$key;
  readonly children?: ReactNode;
  readonly userProfileRef: ProfileLayout_userProfile$key;
};

export const ProfileLayout = ({
  accountRef,
  children,
  userProfileRef,
}: ProfileLayoutProps) => {
  const isAboveMediumBreakpoint = useBreakpoint('md');
  const userAccountRef = useFragment<ProfileLayout_userAccount$key>(
    graphql`
      fragment ProfileLayout_userAccount on UserAccount {
        id
        ...useUserInterfaceFlags_fragment
        ...UncontrolledHeadlineEditor_userAccount
        ...NewProfileSidebar_userAccount
      }
    `,
    accountRef,
  );

  const userProfile = useFragment<ProfileLayout_userProfile$key>(
    graphql`
      fragment ProfileLayout_userProfile on UserProfile {
        displayUsername
        visitorCanEdit
        title
        hasActivePortfolio
        hasCompletedClientOnboarding
        hasCompletedIndyOnboarding
        profileDisplayType
        receivedTestimonials {
          edges {
            node {
              recommend
            }
          }
        }
        recommendationRequests {
          count
        }
        introductionVideo {
          isEnabled
          video {
            uid
          }
        }
        avatarImage {
          id
          url
          uid
        }
        ...WorkTab_userProfile
        ...ServicesTab_userProfile
        ...OpportunitiesTab_userProfile
        ...RecommendationsTab_userProfile
        ...UncontrolledHeadlineEditor_userProfile
        ...NewProfileSidebar_userProfile
      }
    `,
    userProfileRef,
  );

  const {
    displayUsername,
    hasActivePortfolio,
    hasCompletedClientOnboarding,
    hasCompletedIndyOnboarding,
    receivedTestimonials,
    recommendationRequests,
    visitorCanEdit,
  } = userProfile;

  const navigation = useNavigation();
  const [shouldShowInquiryModal, setShouldShowInquiryModal] = useState(true);
  const { isContractor } = useSelectedUserType();

  const { isModalOpen, openMessageSentModal } = useMessageSentModal({
    onClose: () => {
      setShouldShowInquiryModal(false);
      navigation.replace(
        getRoute('/@username', {
          args: {
            username: displayUsername,
          },
          search: {},
        }),
      );
    },
  });

  const [urlSearchParameters] = useURLParameters('/@username');
  const inquiry = useMemo(() => {
    return urlSearchParameters.inquiry;
  }, [urlSearchParameters.inquiry]);

  // Using an useState variable in the useEffect to make sure there are no race conditions
  // between the modal being open and the url search parameters being updated
  useEffect(() => {
    if (inquiry && shouldShowInquiryModal && !isModalOpen) {
      setShouldShowInquiryModal(true);
      openMessageSentModal();
    }
  }, [inquiry, isModalOpen, openMessageSentModal, shouldShowInquiryModal]);

  const hasPermissions = useHasUserFeatures(['RATE_USERS']);

  const positiveRecommendationsCount =
    receivedTestimonials?.edges?.filter?.(
      ({ node: { recommend } }) => recommend,
    )?.length ?? 0;

  const totalPositiveRecommendationsCount =
    positiveRecommendationsCount + recommendationRequests.count;

  const { shouldShowRecommendationsTab } = resolveRecommendationsVisibility({
    hasPositiveRecommendations: totalPositiveRecommendationsCount > 0,
    visitorCanEdit,
  });

  const isBlogProfile = userProfile.profileDisplayType === 'BLOG';
  const currentUser = useCurrentUser();

  const { wasRemovedFromClientWorkspace } = useWorkspaces();

  const isMobile = useIsMobile();

  const showProfileInsightsButton = visitorCanEdit && isContractor;

  return (
    <ProfileImageCropperContextProvider>
      <MainLayout
        backgroundColor="white"
        username={userProfile.displayUsername}
      >
        {showProfileInsightsButton ? (
          <MobileViewInsightsContainer>
            <MobileDiscoveryInsightsButton />
          </MobileViewInsightsContainer>
        ) : null}
        <RelativePositionContainer>
          <Section>
            <GridColumn
              initial={12}
              lg={4}
              minWidth={isMobile ? undefined : 340}
            >
              <NewProfileSidebar
                userAccountRef={userAccountRef}
                userProfileRef={userProfile}
              />
            </GridColumn>
            {userProfile ? (
              <GridColumn
                initial={12}
                lg={8}
              >
                <ProfileBody>
                  <ProfileContent>
                    <Suspense>
                      {userProfile.visitorCanEdit ? (
                        <UncontrolledHeadlineEditor
                          accountRef={userAccountRef}
                          userProfile={userProfile}
                        />
                      ) : (
                        <ProfileTitleContainer>
                          <ProfileTitle>{userProfile.title}</ProfileTitle>
                        </ProfileTitleContainer>
                      )}
                    </Suspense>

                    {isBlogProfile ? null : (
                      <ProfileRoutingTabs>
                        <RoutingTabs.List aria-label="Profile Tabs">
                          <>
                            {!hasCompletedIndyOnboarding &&
                            !hasCompletedClientOnboarding ? (
                              <WorkTab userProfileRef={userProfile} />
                            ) : null}
                            {hasCompletedIndyOnboarding ? (
                              <>
                                <WorkTab userProfileRef={userProfile} />
                                <ServicesTab userProfileRef={userProfile} />
                              </>
                            ) : null}
                            {hasCompletedClientOnboarding &&
                            !wasRemovedFromClientWorkspace ? (
                              <OpportunitiesTab userProfileRef={userProfile} />
                            ) : null}
                            {shouldShowRecommendationsTab ? (
                              <RecommendationsTab
                                accountRef={userAccountRef}
                                userProfileRef={userProfile}
                              />
                            ) : null}
                            {showProfileInsightsButton &&
                            isAboveMediumBreakpoint ? (
                              <FinalListItem>
                                <DiscoveryInsightsButton />
                              </FinalListItem>
                            ) : null}
                          </>
                        </RoutingTabs.List>
                      </ProfileRoutingTabs>
                    )}
                    <Main>
                      {visitorCanEdit && isContractor ? (
                        <Suspense>
                          <ExplorePortfoliosCallout />
                        </Suspense>
                      ) : null}

                      {children}
                    </Main>
                  </ProfileContent>
                </ProfileBody>
              </GridColumn>
            ) : (
              <AccountDoesNotExist
                currentUsername={currentUser?.profile.displayUsername}
              />
            )}
          </Section>

          {hasPermissions ? (
            <Suspense fallback={<span>...</span>}>
              <AdminActions username={displayUsername} />
            </Suspense>
          ) : null}
        </RelativePositionContainer>
      </MainLayout>
      <Suspense>
        {userProfile.visitorCanEdit ? <ProfileImageCropperModal /> : null}
      </Suspense>

      {visitorCanEdit ? (
        <Suspense>
          <ProfileCompletionChecklist
            variant={hasActivePortfolio ? 'discover' : 'launch'}
          />
        </Suspense>
      ) : null}
    </ProfileImageCropperContextProvider>
  );
};
